import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import styled, { useTheme } from 'styled-components'

import { cardFrameCSS } from '@/@bothrs-ui/web/components/card/Card'
import { Flex } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { openUrl } from '@/utils/external-resources'

import type { ComponentPropsWithoutRef, MouseEvent } from 'react'

type SelectMealCardProps = ComponentPropsWithoutRef<'button'> & {
  /** The CMeal sign-in link; the page resolves it, as it costs a Synops call. */
  href: string
}

/**
 * The CMeal link on the admission detail: the title with its icon on one line, the description with
 * the external-link mark on the next. The whole card is the button; it opens the link in a new
 * window after the page's own click handler, which is where the tracking lives.
 */
const SelectMealCard = ({ href, onClick, ...props }: SelectMealCardProps) => {
  const theme = useTheme()

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
    openUrl(href, { windowName: '_blank' })
  }

  return (
    <StCard type='button' onClick={handleClick} {...props}>
      <Flex alignItems='center' gap={theme.UI.Spacing.Space2}>
        <StIcon icon={['fal', 'utensils']} />
        <Span type='BodyMediumBold'>
          {t({
            id: 'select-meal-card.title',
            message: 'Kies zelf je menu',
          })}
        </Span>
      </Flex>
      <Flex alignItems='center' justifyContent='space-between' gap={theme.UI.Spacing.Space2}>
        <Span type='BodyMediumRegular'>
          {t({
            id: 'select-meal-card.body',
            message: 'Stel zelf je maaltijden samen',
          })}
        </Span>
        <StIcon icon={['fal', 'external-link']} color={theme.Components.Button.Tertiary.Text} />
      </Flex>
    </StCard>
  )
}

export { SelectMealCard }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

// The design gives the card a wider left inset than the other cards, so the icon lines up with the
// text of the cards above it. The card is a button: its own look is reset, and a keyboard focus draws
// a ring in place of the outline.
const StCard = styled.button`
  margin: 0;
  border: 0;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  ${cardFrameCSS()}
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};
  width: 100%;
  padding-left: ${({ theme }) => theme.UI.SpacingPx.Space5};
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.Components.Button.Primary.Fill};
  }
`

const StIcon = styled(FontAwesomeIcon)`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
`
